import styled, { css } from 'styled-components';
import {
	AdvancedSwitchAlign,
	AdvancedSwitchJustify,
	AdvancedSwitchSize,
	SwitchState,
} from './AdvancedSwitch.types';

const switchWidths = {
	s: '2.25rem',
	m: '2.75rem',
};
const switchHeights = {
	s: '1.25rem',
	m: '1.5rem',
};
const thumbSizes = {
	s: '1rem',
	m: '1.25rem',
};

export const AdvancedSwitchInput = styled.div<{
	state: SwitchState;
	size: AdvancedSwitchSize;
	twoWay: boolean;
}>`
	--adv-switch-width: ${({ size }) => switchWidths[size]};
	--adv-switch-height: ${({ size }) => switchHeights[size]};
	--adv-thumb-size: ${({ size }) => thumbSizes[size]};
	--adv-thumb-offset: 2px;
	--thumbColor: ${(props) => props.theme.v3.colors.white[0]};

	// Track
	display: flex;
	align-items: center;
	flex-shrink: 0;
	width: var(--adv-switch-width);
	height: var(--adv-switch-height);
	border-radius: 1rem;
	position: relative;
	cursor: pointer;
	transition: background-color 0.2s ease-in-out;

	// Thumb
	&::before {
		content: '';
		display: block;
		position: absolute;
		width: var(--adv-thumb-size);
		height: var(--adv-thumb-size);
		transition: left 0.2s ease-in-out;
		border-radius: 50%;
		left: var(--adv-thumb-offset);
	}

	${({ state, twoWay }) => {
		if (state === SwitchState.LEFT) {
			return css`
				background-color: ${({ theme }) =>
					twoWay ? theme.v3.colors.blue[6] : theme.v3.colors.gray[8]};

				&::before {
					background-color: var(--thumbColor);
				}

				&:hover {
					background-color: ${({ theme }) =>
						twoWay ? theme.v3.colors.blue[8] : theme.v3.colors.gray[4]};
				}
			`;
		} else if (state === SwitchState.RIGHT) {
			return css`
				background-color: ${({ theme }) => theme.v3.colors.blue[6]};

				&::before {
					background-color: var(--thumbColor);
					left: calc(100% - var(--adv-thumb-size) - var(--adv-thumb-offset));
				}

				&:hover {
					background-color: ${({ theme }) => theme.v3.colors.blue[8]};
				}
			`;
		} else if (state === SwitchState.MIDDLE) {
			return css`
				background-color: ${({ theme }) => theme.v3.colors.gray[8]};

				&::before {
					background-color: var(--thumbColor);
					left: calc(50% - var(--adv-thumb-size) / 2);
				}
			`;
		}
	}};
`;

// Visually-hidden but focusable/clickable real checkbox that backs the switch.
// Kept in the a11y tree (not display:none) so keyboard + screen readers work;
// the wrapping <label> forwards clicks on the track/labels to it.
export const AdvancedSwitchCheckbox = styled.input`
	position: absolute;
	width: 1px;
	height: 1px;
	margin: 0;
	padding: 0;
	overflow: hidden;
	white-space: nowrap;
	opacity: 0;
	cursor: inherit;
`;

export const AdvancedSwitchStyles = styled.div<{
	disabled?: boolean;
	justify: AdvancedSwitchJustify;
	align: AdvancedSwitchAlign;
}>`
	display: flex;
	justify-content: ${({ justify }) => justify};
	align-items: ${({ align }) => align};
	gap: 0.75rem;
	position: relative;
	cursor: pointer;
	transition: all 2s ease-in-out;

	.mantine-Text-root {
		white-space: nowrap;
	}

	// Keyboard focus ring on the track when the hidden checkbox is focused.
	${AdvancedSwitchCheckbox}:focus-visible ~ ${AdvancedSwitchInput} {
		outline: 2px solid ${({ theme }) => theme.v3.colors.blue[6]};
		outline-offset: 2px;
	}

	${({ disabled }) =>
		disabled &&
		css`
			cursor: not-allowed;

			${AdvancedSwitchInput} {
				cursor: not-allowed;
				opacity: 0.3;
				pointer-events: none;
			}
		`};
`;
